import React, { useState, useEffect } from "react";
import { Mail, X, Check } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const COPY = "Get the WorldAI Weekly — real, sourced AI data every Friday. Free.";
const BAR_DISMISS_KEY = "waii_newsletter_bar_dismissed";

// Reusable newsletter capture. variant="bar" is a slim dismissible strip;
// variant="inline" is a card box placed after high-engagement features.
export default function NewsletterSignup({ variant = "inline", source = "site", testid }) {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    if (variant === "bar") setHidden(localStorage.getItem(BAR_DISMISS_KEY) === "1");
  }, [variant]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || busy) return;
    setBusy(true);
    try {
      await api.post("/newsletter/subscribe", { email });
      setDone(true);
      setEmail("");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const dismiss = () => {
    localStorage.setItem(BAR_DISMISS_KEY, "1");
    setHidden(true);
  };

  if (variant === "bar") {
    if (hidden) return null;
    return (
      <div data-testid={testid || "newsletter-bar"}
        className="relative border-y border-[#00F0FF]/20 bg-[#00F0FF]/[0.06]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-3 flex flex-col sm:flex-row items-center justify-center gap-3 pr-10">
          <p className="flex items-center gap-2 text-sm text-[#cbd5e1] text-center">
            <Mail className="w-4 h-4 text-[#00F0FF] shrink-0 hidden sm:block" />
            <span><span className="font-semibold text-white">Get the WorldAI Weekly</span> — real, sourced AI data every Friday. Free.</span>
          </p>
          {done ? (
            <span className="flex items-center gap-1.5 text-sm text-[#4ade80] font-medium" data-testid="newsletter-bar-success">
              <Check className="w-4 h-4" /> You're in — first issue arrives Friday.
            </span>
          ) : (
            <form onSubmit={submit} className="flex gap-2 w-full sm:w-auto">
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com" data-testid="newsletter-bar-email"
                className="flex-1 sm:w-56 bg-[#0B0F19] border border-white/10 rounded-md px-3 py-2 text-sm focus:border-[#00F0FF] focus:outline-none" />
              <button type="submit" disabled={busy} data-testid="newsletter-bar-submit"
                className="bg-[#00F0FF] text-[#04121a] font-semibold px-4 py-2 rounded-md hover:brightness-110 transition disabled:opacity-50 text-sm whitespace-nowrap">
                {busy ? "…" : "Subscribe"}
              </button>
            </form>
          )}
        </div>
        <button onClick={dismiss} data-testid="newsletter-bar-dismiss" aria-label="Dismiss"
          className="absolute top-1/2 -translate-y-1/2 right-3 md:right-6 text-[#64748B] hover:text-white transition">
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  // inline card
  return (
    <div data-testid={testid || "newsletter-inline"}
      className="rounded-2xl border border-[#00F0FF]/20 bg-[#0e1424] p-6 md:p-8 grid-bg">
      <div className="flex items-start gap-4 flex-col md:flex-row md:items-center md:justify-between">
        <div className="flex items-start gap-3">
          <div className="shrink-0 w-10 h-10 rounded-xl bg-[#00F0FF]/15 flex items-center justify-center">
            <Mail className="w-5 h-5 text-[#00F0FF]" />
          </div>
          <div>
            <h3 className="font-display font-bold text-lg md:text-xl text-white">Get the WorldAI Weekly</h3>
            <p className="text-sm text-[#94A3B8] mt-0.5">Real, sourced AI data every Friday. Free.</p>
          </div>
        </div>
        {done ? (
          <span className="flex items-center gap-1.5 text-sm text-[#4ade80] font-medium" data-testid="newsletter-inline-success">
            <Check className="w-4 h-4" /> You're in — first issue arrives Friday.
          </span>
        ) : (
          <form onSubmit={submit} className="flex gap-2 w-full md:w-auto">
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com" data-testid="newsletter-inline-email"
              className="flex-1 md:w-60 bg-[#0B0F19] border border-white/10 rounded-md px-3 py-2.5 text-sm focus:border-[#00F0FF] focus:outline-none" />
            <button type="submit" disabled={busy} data-testid="newsletter-inline-submit"
              className="bg-[#00F0FF] text-[#04121a] font-semibold px-5 py-2.5 rounded-md hover:brightness-110 transition disabled:opacity-50 text-sm whitespace-nowrap">
              {busy ? "…" : "Subscribe"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
